import * as React from 'react'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import PreviewIcon from '@mui/icons-material/Preview'
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowId,
  GridToolbar,
} from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import { BadgeModel } from 'types'
import axios from 'axios'
import { base_url } from 'env'
import { toast } from 'react-hot-toast'
import { Box } from '@mui/material'
import SearchBar from './SearchBar'
import ExportButton from './ExportButton'

const searchFields = [
  { value: 'name', label: 'Nom' },
  { value: 'description', label: 'Description' },
  { value: 'unlockCondition', label: 'Condition de Déblocage' },
  { value: 'id', label: 'ID' },
]

export default function BadgeDataGrid({ badges }: { badges: BadgeModel[] }) {
  const [rows, setRows] = React.useState(badges)
  const navigate = useNavigate()

  React.useEffect(() => {
    setRows(badges)
  }, [badges])

  const handleDeleteClick = (id: GridRowId) => () => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ce badge ?`)) {
      axios
        .delete(`${base_url}/badge/${id}`)
        .then(() => {
          toast.success('Badge supprimé avec succès.')
          setRows((prevRows) => prevRows.filter((row) => row.id !== id))
        })
        .catch((err) => {
          console.error(err)
        })
    }
  }

  const handleEditClick = (id: GridRowId) => () => {
    navigate(`/badge/${id}/edit`)
  }

  const handlePreviewClick = (id: GridRowId) => () => {
    navigate(`/badge/${id}`)
  }

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Nom', flex: 1, minWidth: 200 },
    {
      field: 'unlockCondition',
      headerName: 'Condition de Déblocage',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'unlockValue',
      headerName: 'Valeur Requise',
      type: 'number',
      width: 150,
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 250,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      cellClassName: 'actions',
      getActions: ({ id }) => [
        <GridActionsCellItem
          icon={<PreviewIcon />}
          label="Aperçu"
          onClick={handlePreviewClick(id)}
          color="inherit"
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Modifier"
          onClick={handleEditClick(id)}
          color="inherit"
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Supprimer"
          onClick={handleDeleteClick(id)}
          color="inherit"
        />,
      ],
    },
  ]

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          margin: '1rem',
          gap: '1rem',
        }}
      >
        <SearchBar
          onSearch={setRows}
          fields={searchFields}
          originalData={badges}
        />
        <ExportButton
          fields={[
            'id',
            'name',
            'description',
            'unlockCondition',
            'unlockValue',
            'unlockAuxilliaryValue',
          ]}
          data={badges}
          exportFileName="badges_export"
        />
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        slots={{ toolbar: GridToolbar }}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
      />
    </Box>
  )
}
